import { supabase } from "@/integrations/supabase/client";

/**
 * Public VAPID key — safe in the client. Must match VAPID_PUBLIC_KEY on poll-turnover-clocks.
 * Override with VITE_VAPID_PUBLIC_KEY in build env when rotating keys.
 */
const VAPID_PUBLIC =
  (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined)?.trim() ||
  "BKy8IvRymIgRk_SOtEGddT94VNbv4IUyfgxRq_WQid1P5qfOND3ErbqvCJPheVL6L_nfB0OYpcQcluon8L5QXGA";

export const JOB_ALERTS_ENABLED_KEY = "stravo_job_alerts_enabled_v1";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isJobPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function hasVapidPublicKey(): boolean {
  return Boolean(VAPID_PUBLIC);
}

export type JobPushStatus = {
  supported: boolean;
  vapidConfigured: boolean;
  permission: NotificationPermission | "unsupported";
  subscribed: boolean;
  enabledLocally: boolean;
};

export async function getJobPushStatus(userId?: string | null): Promise<JobPushStatus> {
  const supported = isJobPushSupported();
  const vapidConfigured = hasVapidPublicKey();
  let enabledLocally = false;
  try {
    enabledLocally = localStorage.getItem(JOB_ALERTS_ENABLED_KEY) === "1";
  } catch {
    /* ignore */
  }

  if (!supported) {
    return {
      supported: false,
      vapidConfigured,
      permission: "unsupported",
      subscribed: false,
      enabledLocally,
    };
  }

  const permission = Notification.permission;
  let subscribed = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) subscribed = true;
  } catch {
    /* ignore */
  }

  if (!subscribed && userId) {
    const { count } = await supabase
      .from("push_subscriptions")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);
    subscribed = (count ?? 0) > 0;
  }

  return {
    supported,
    vapidConfigured,
    permission,
    subscribed,
    enabledLocally: enabledLocally || subscribed,
  };
}

/** Request notification permission + save Home Screen push subscription. */
export async function enableJobPushNotifications(
  userId: string,
): Promise<"ok" | "denied" | "unsupported" | "no_vapid"> {
  if (!isJobPushSupported()) return "unsupported";
  if (!VAPID_PUBLIC) return "no_vapid";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });
  }

  const json = sub.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) return "unsupported";

  const { error } = await supabase.from("push_subscriptions").upsert(
    {
      user_id: userId,
      endpoint,
      p256dh,
      auth,
      user_agent: navigator.userAgent.slice(0, 240),
      updated_at: new Date().toISOString(),
    },
    { onConflict: "user_id,endpoint" },
  );
  if (error) throw error;

  try {
    localStorage.setItem(JOB_ALERTS_ENABLED_KEY, "1");
  } catch {
    /* ignore */
  }
  return "ok";
}

/** Turn off job alerts: unsubscribe device + remove saved endpoints for this user. */
export async function disableJobPushNotifications(userId: string): Promise<void> {
  try {
    if (isJobPushSupported()) {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }
  } catch {
    /* still clear DB */
  }

  await supabase.from("push_subscriptions").delete().eq("user_id", userId);

  try {
    localStorage.removeItem(JOB_ALERTS_ENABLED_KEY);
  } catch {
    /* ignore */
  }
}
